import React, { useState, useEffect, useRef } from "react";
import "./ChatBot.css";
import { Send, X, Bot, Minimize2 } from "lucide-react";
import { ENDPOINTS } from "../api/api";
import { useTranslation } from "react-i18next";
import "../i18n";

const ChatBot = () => {
  const { t, i18n } = useTranslation();

  const QUICK_REPLIES = [
    { label: t("chat.quickServices"),  text: "services"  },
    { label: t("chat.quickPortfolio"), text: "portfolio" },
    { label: t("chat.quickContact"),   text: "contact"   },
    { label: t("chat.quickHello"),     text: "hello"     },
  ];

  const [isOpen, setIsOpen]       = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [messages, setMessages]   = useState([
    {
      sender: "bot",
      text: t("chat.welcome"),
      time: new Date(),
    },
  ]);
  const [input, setInput]       = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [unread, setUnread]     = useState(0);
  const messagesEndRef          = useRef(null);
  const inputRef                = useRef(null);

  // Reset welcome message when language changes
  useEffect(() => {
    setMessages([{
      sender: "bot",
      text: t("chat.welcome"),
      time: new Date(),
    }]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  useEffect(() => {
    if (isOpen && !minimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
      setUnread(0);
    }
  }, [isOpen, minimized]);

  const formatTime = (date) =>
    date?.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  const sendMessage = async (text) => {
    const trimmed = text.trim();
    if (!trimmed) return;

    setMessages((prev) => [...prev, { sender: "user", text: trimmed, time: new Date() }]);
    setInput("");
    setIsTyping(true);

    try {
      const lang = (i18n.language || "en").split("-")[0].toLowerCase();
      const response = await fetch(ENDPOINTS.chatbotAsk, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: trimmed, lang }),
      });
      const data = await response.json();

      setIsTyping(false);
      setMessages((prev) => [
        ...prev,
        { sender: "bot", text: data.reply, time: new Date() },
      ]);
      if (!isOpen || minimized) setUnread((n) => n + 1);

    } catch (err) {
      console.error("Chatbot error:", err);
      setIsTyping(false);
      setMessages((prev) => [
        ...prev,
        {
          sender: "bot",
          text: "⚠️ Something went wrong. Please try again or email us at info@solviotech.com",
          time: new Date(),
        },
      ]);
    }
  };

  const handleSend = () => sendMessage(input);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    setMinimized(false);
    setUnread(0);
  };

  const renderText = (text) => {
    const parts = text.split(/\*\*(.*?)\*\*/g);
    return parts.map((part, i) =>
      i % 2 === 1 ? <strong key={i}>{part}</strong> : part
    );
  };

  return (
    <div className="cb-container">
      {!isOpen && (
        <button className="cb-toggle" onClick={handleOpen} aria-label="Open chat">
          <div className="cb-toggle-icon">
            <Bot size={26} />
          </div>
          {unread > 0 && <span className="cb-badge">{unread}</span>}
          <span className="cb-toggle-label">{t("chat.toggle")}</span>
        </button>
      )}

      {isOpen && (
        <div className={`cb-box ${minimized ? "cb-box--minimized" : ""}`}>
          <div className="cb-header">
            <div className="cb-header-left">
              <div className="cb-avatar">
                <Bot size={18} />
                <span className="cb-online-dot"></span>
              </div>
              <div className="cb-header-info">
                <span className="cb-header-name">Solvio</span>
                <span className="cb-header-status">
                  <span className="cb-status-dot"></span>
                  {t("chat.status")}
                </span>
              </div>
            </div>
            <div className="cb-header-actions">
              <button
                className="cb-header-btn"
                onClick={() => setMinimized((v) => !v)}
                aria-label={minimized ? "Expand" : "Minimize"}
              >
                <Minimize2 size={14} />
              </button>
              <button
                className="cb-header-btn cb-header-btn--close"
                onClick={() => setIsOpen(false)}
                aria-label="Close chat"
              >
                <X size={14} />
              </button>
            </div>
          </div>

          {!minimized && (
            <>
              <div className="cb-messages">
                {messages.map((msg, i) => (
                  <div key={i} className={`cb-msg cb-msg--${msg.sender}`}>
                    {msg.sender === "bot" && (
                      <div className="cb-msg-avatar">
                        <Bot size={13} />
                      </div>
                    )}
                    <div className="cb-msg-bubble">
                      <p className="cb-msg-text">{renderText(msg.text)}</p>
                      <span className="cb-msg-time">{formatTime(msg.time)}</span>
                    </div>
                  </div>
                ))}

                {isTyping && (
                  <div className="cb-msg cb-msg--bot">
                    <div className="cb-msg-avatar">
                      <Bot size={13} />
                    </div>
                    <div className="cb-msg-bubble cb-typing">
                      <span></span><span></span><span></span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="cb-quick-replies">
                {QUICK_REPLIES.map((q) => (
                  <button
                    key={q.text}
                    className="cb-quick-btn"
                    onClick={() => sendMessage(q.text)}
                  >
                    {q.label}
                  </button>
                ))}
              </div>

              <div className="cb-input-row">
                <input
                  ref={inputRef}
                  type="text"
                  className="cb-input"
                  placeholder={t("chat.placeholder")}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                <button
                  className={`cb-send-btn ${input.trim() ? "cb-send-btn--active" : ""}`}
                  onClick={handleSend}
                  disabled={!input.trim()}
                  aria-label="Send message"
                >
                  <Send size={16} />
                </button>
              </div>

              <div className="cb-footer-bar">
                <span>{t("chat.poweredBy")}</span>
                <strong>Solviotech</strong>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatBot;